import { Flex } from 'antd';
import Icon from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import styles from './business-hours-mobile.module.scss';
import { Button } from '../button/button';
import ArrowLeft from '@/assets/icons/angle-left-solid-full.svg?react';
import ArrowRight from '@/assets/icons/angle-right-solid-full.svg?react';
import EditIcon from '@/assets/icons/pencil-solid-full.svg?react';
import { BusinessHoursLegend } from '../business-hours-legend/business-hours-legend';
import { BusinessHours } from '@/services/business-hours/business-hours.types';
import { HoursBadge, HoursBadgeType } from '../hours-badge/hours-badge';

dayjs.extend(isoWeek);
dayjs.extend(utc);

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

type BusinessHoursMobileProps = {
  businessHoursData: BusinessHours | undefined;
  currentMonth: Dayjs;
  onMonthChange: (date: Dayjs) => void;
  onDateSelect: (date: Dayjs) => void;
};

export function BusinessHoursMobile({
  businessHoursData,
  currentMonth,
  onMonthChange,
  onDateSelect,
}: BusinessHoursMobileProps) {
  const { permanent = [], temporary = [] } = businessHoursData || {};

  const [weekStart, setWeekStart] = useState<Dayjs>(() => currentMonth.startOf('isoWeek'));

  useEffect(() => {
    setWeekStart(currentMonth.startOf('isoWeek'));
  }, [currentMonth]);

  const weekEnd = weekStart.add(6, 'day');

  const goToPreviousWeek = () => {
    const newWeekStart = weekStart.subtract(1, 'week');
    setWeekStart(newWeekStart);
    if (newWeekStart.month() !== weekStart.month()) {
      onMonthChange(newWeekStart);
    }
  };

  const goToNextWeek = () => {
    const newWeekStart = weekStart.add(1, 'week');
    setWeekStart(newWeekStart);
    if (newWeekStart.month() !== weekStart.month()) {
      onMonthChange(newWeekStart);
    }
  };

  const extractTime = (isoString: string) => {
    const match = isoString.match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '';
  };

  const getHoursForDay = (dayIndex: number): { hours: string[]; type: HoursBadgeType } => {
    const currentDate = weekStart.add(dayIndex, 'day');
    const dateStr = currentDate.format('YYYY-MM-DD');
    const weekday = WEEKDAYS[dayIndex].toLowerCase();

    const temporaryHoursArray = temporary.filter((item) => {
      if (!item.start_time) return false;
      const itemDate = dayjs(item.start_time).format('YYYY-MM-DD');
      return itemDate === dateStr;
    });

    if (temporaryHoursArray.length > 0) {
      const allClosed = temporaryHoursArray.every((period) => period.is_closed);
      if (allClosed) {
        return { hours: ['Closed'], type: 'closed' };
      }

      const validPeriods = temporaryHoursArray.filter(
        (period) => !period.is_closed && period.start_time && period.end_time
      );
      if (validPeriods.length === 0) {
        return { hours: ['Closed'], type: 'closed' };
      }

      return {
        hours: validPeriods.map(
          (period) => `${extractTime(period.start_time!)} – ${extractTime(period.end_time!)}`
        ),
        type: 'temporary',
      };
    }

    const permanentHoursArray = permanent.filter((item) => item.wday === weekday);

    if (permanentHoursArray.length > 0) {
      const allClosed = permanentHoursArray.every((period) => period.is_closed);
      if (allClosed) {
        return { hours: ['Closed'], type: 'closed' };
      }

      const validPeriods = permanentHoursArray.filter(
        (period) => !period.is_closed && period.start_time && period.end_time
      );
      if (validPeriods.length === 0) {
        return { hours: ['Closed'], type: 'closed' };
      }

      return {
        hours: validPeriods.map(
          (period) => `${extractTime(period.start_time!)} – ${extractTime(period.end_time!)}`
        ),
        type: 'permanent',
      };
    }

    return { hours: ['Closed'], type: 'closed' };
  };

  return (
    <div>
      <Flex align="center" justify="space-between" className={styles.header}>
        <Button buttonVariant="secondary" onClick={goToPreviousWeek} className={styles.navButton}>
          <Icon
            component={() => (
              <ArrowLeft className={styles.navArrowButtonIcon} width="20" height="20" />
            )}
          />
        </Button>
        <span className={styles.dateRange}>
          {weekStart.format('MMM D')} – {weekEnd.format('MMM D, YYYY')}
        </span>
        <Button buttonVariant="secondary" onClick={goToNextWeek} className={styles.navButton}>
          <Icon
            component={() => (
              <ArrowRight className={styles.navArrowButtonIcon} width="20" height="20" />
            )}
          />
        </Button>
      </Flex>

      <BusinessHoursLegend />

      <div className={styles.daysList}>
        {WEEKDAYS.map((day, index) => {
          const { hours, type } = getHoursForDay(index);
          const currentDate = weekStart.add(index, 'day');
          const isToday = currentDate.isSame(dayjs(), 'day');

          return (
            <Flex
              key={day}
              justify="space-between"
              align="center"
              className={`${styles.dayRow} ${isToday ? styles.today : ''}`}
            >
              <Flex vertical>
                <p className={styles.dayName}>
                  {day} <span className={styles.dayDate}>{isToday ? '(Today)' : ''}</span>
                </p>
                <span className={`${styles.dayDate} ${isToday ? styles.today : ''}`}>
                  {currentDate.format('D MMM')}
                </span>
              </Flex>
              <Flex gap={8} align="center">
                <Flex vertical gap={4}>
                  {hours.map((timeRange) => (
                    <HoursBadge key={timeRange} hours={timeRange} type={type} variant="mobile" />
                  ))}
                </Flex>

                <Button
                  buttonVariant="link"
                  size="small"
                  disabled={currentDate.isBefore(dayjs(), 'day')}
                  className={styles.editButton}
                  onClick={() => onDateSelect(currentDate)}
                >
                  <Icon component={() => <EditIcon width="16" height="16" />} />
                </Button>
              </Flex>
            </Flex>
          );
        })}
      </div>
    </div>
  );
}
